/**
 * LogoutConfirmModal - Ruthless Coach persona logout confirmation
 * 
 * "Are you sure you want to leave your destiny behind?"
 */
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, X } from 'lucide-react';

const LogoutConfirmModal = ({ isOpen, onConfirm, onCancel, isLoading }) => {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {/* Backdrop */}
                    <motion.div 
                        className="absolute inset-0 bg-black/80 backdrop-blur-md"
                        onClick={onCancel}
                    />

                    {/* Modal */}
                    <motion.div
                        className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl"
                        style={{
                            background: 'linear-gradient(180deg, rgba(20, 10, 15, 0.98) 0%, rgba(10, 5, 8, 0.98) 100%)',
                            border: '1px solid rgba(180, 50, 50, 0.3)',
                            boxShadow: '0 0 60px rgba(180, 50, 50, 0.15)'
                        }}
                        initial={{ scale: 0.9, y: 30, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.9, y: 30, opacity: 0 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    >
                        {/* Close Button */}
                        <button
                            onClick={onCancel}
                            className="absolute top-4 right-4 p-2 rounded-full transition-colors z-20"
                            style={{ 
                                background: 'rgba(212, 175, 55, 0.1)',
                                color: 'rgba(212, 175, 55, 0.6)'
                            }}
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="p-8 pt-10">
                            {/* Ruthless Icon */}
                            <div className="flex justify-center mb-6">
                                <div 
                                    className="w-20 h-20 rounded-full flex items-center justify-center"
                                    style={{
                                        background: 'radial-gradient(circle, rgba(180, 50, 50, 0.2) 0%, transparent 70%)',
                                        border: '2px solid rgba(180, 50, 50, 0.4)'
                                    }}
                                >
                                    <LogOut className="w-8 h-8 text-red-400" />
                                </div>
                            </div>

                            {/* Ruthless Coach Headline */}
                            <h2 
                                className="font-cinzel text-xl text-center mb-3"
                                style={{ color: '#D4AF37' }}
                            >
                                Abandoning Your Destiny?
                            </h2>

                            {/* Ruthless Coach Message */}
                            <p className="text-center text-primary/70 font-inter text-sm mb-2 leading-relaxed">
                                The stars don&apos;t forget those who turn their backs.
                            </p>
                            <p className="text-center text-primary/50 font-inter text-xs mb-6 italic">
                                Your chart, your cosmic insights, your progress...
                                <br />all will be erased. No second chances.
                            </p>

                            {/* Warning */}
                            <div 
                                className="mb-6 p-3 rounded-lg text-center"
                                style={{
                                    background: 'rgba(180, 50, 50, 0.1)',
                                    border: '1px solid rgba(180, 50, 50, 0.2)'
                                }}
                            >
                                <p className="text-red-400/80 text-xs font-inter">
                                    This will clear all your data and sign you out.
                                </p>
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-3">
                                <motion.button
                                    onClick={onCancel}
                                    className="flex-1 py-3 px-4 rounded-xl font-inter text-sm font-medium transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #D4AF37 0%, #AA8C2C 100%)',
                                        color: '#0A0A0B'
                                    }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    Stay With The Stars
                                </motion.button>
                                
                                <motion.button
                                    onClick={onConfirm}
                                    disabled={isLoading}
                                    className="flex-1 py-3 px-4 rounded-xl font-inter text-sm transition-all disabled:opacity-50"
                                    style={{
                                        background: 'rgba(180, 50, 50, 0.2)',
                                        border: '1px solid rgba(180, 50, 50, 0.4)',
                                        color: '#E57373'
                                    }}
                                    whileHover={{ 
                                        scale: 1.02,
                                        backgroundColor: 'rgba(180, 50, 50, 0.3)'
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                    data-testid="confirm-logout-btn"
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                                            Leaving...
                                        </span>
                                    ) : (
                                        'Leave Anyway'
                                    )}
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default LogoutConfirmModal;
